import type { TFunction } from 'i18next';

import type { AdsPlatform, GroupLibraryItem, LibraryItemSource } from '@/types';
import type { CheckBadge } from '@/types/check';

/**
 * 素材庫清單呈現層純函式(R209;對照 D3 library.js 的 srcName/sorted/checkRank)。
 * 從 LibraryItemsSection 抽出以利單測與拆檔(R206 慣例)。
 */

/** 顯示名稱:metadata 候選鍵 → itemId fallback(既有邏輯搬移)。 */
export function itemDisplayName(it: GroupLibraryItem): string {
  const m = it.metadata;
  const cand = [m.name, m.filename, m.display_label, m.title];
  for (const c of cand) if (typeof c === 'string' && c.trim()) return c;
  return it.itemId;
}

/**
 * 「來源名稱」欄(設計 srcName:ai→專案名 / up→檔名 / ads→帳戶名)。
 * 資料映射(缺 BE 資料回 null → 呼叫端顯「—」):
 *   - upload → `metadata.filename`
 *   - ad_account_import → `sourceAdAccountCid`(帳戶名稱欄 BE 未提供,先顯 cid)
 *   - project_asset → `sourceProjectName`(R212 接 docs/88 §9;**專案已刪 → null**)
 */
export function itemSrcName(it: GroupLibraryItem): string | null {
  if (it.source === 'upload') {
    const f = it.metadata.filename;
    return typeof f === 'string' && f.trim() ? f : null;
  }
  if (it.source === 'ad_account_import') return it.sourceAdAccountCid;
  return it.sourceProjectName;
}

/**
 * 來源標籤的配色 class(列表 / 燈箱的 `t-*`)。
 *
 * **查表而非 ternary 落尾**:原本三處各寫一條 `project_asset ? … : upload ? … : 't-ads'`,
 * 新增值域(`template_demo`)會**靜默繼承廣告帳戶配色** —— 看起來正常、意思是錯的。
 * 查不到就回空字串(只有基底 `tag` 樣式),寧可沒顏色也不要給錯的顏色。
 *
 * (R217 網格卡重做:訂版拿掉來源角標 → `s-*` 前綴整組退場,不留無 caller 的分支。)
 */
const SOURCE_TAG_SUFFIX: Record<LibraryItemSource, string> = {
  project_asset: 'ai',
  // R270 / ADR-044 — 畫布文案藍卡入庫。**刻意與 `project_asset` 共用 `t-ai`**:兩者都是
  // 「畫布上 AI 產出的成品」,差別只在來源指標是 node 還是 ProjectAsset,對看表的人沒有
  // 意義。這張表已經有四色,再開第五色只會更難讀(型別本來就另有一欄在標)。
  canvas_node: 'ai',
  upload: 'up',
  ad_account_import: 'ads',
  template_demo: 'demo',
};

/**
 * `LibraryItemSource` 的**執行期全集**。`Record<LibraryItemSource, …>` 讓 tsc 強制
 * 這張表窮舉,所以它的 key 就是值域本身 —— 篩選 chip 與契約鎖都吃這一份,不要再抄。
 */
export const LIBRARY_ITEM_SOURCES = Object.keys(SOURCE_TAG_SUFFIX) as LibraryItemSource[];

export function sourceTagClass(source: LibraryItemSource): string {
  const suffix = SOURCE_TAG_SUFFIX[source];
  return suffix ? `t-${suffix}` : '';
}

/**
 * R304 — 這顆**廣告匯入** item 來自哪個平台(BE docs/114 §1)。
 *
 * Meta 匯入的 item 帶 `metadata.platform === "meta_ads"`;Google 匯入的**沒有** `platform` 欄
 * (= Google,BE 對存量零改動)。`source` 值域沒有變(仍是 `ad_account_import`),所以平台只能
 * 從 metadata 看;metadata 是自由 dict → 嚴格比對字串,認不得就當 Google(既有行為)。
 * 非廣告匯入的 item → `null`(呼叫端不畫平台)。
 */
export function itemAdPlatform(it: Pick<GroupLibraryItem, 'source' | 'metadata'>): AdsPlatform | null {
  if (it.source !== 'ad_account_import') return null;
  return it.metadata.platform === 'meta_ads' ? 'meta' : 'google';
}

/**
 * R304 — `metadata.linked_only === true`(docs/114 §1):Meta 影片原檔拿不到,`gcs_uri` 是**縮圖**不是影片。
 * 嚴格 `=== true`(同 `itemSourceUnavailable` 的方向:認不出來就當一般 item,不亂標)。
 */
export function itemLinkedOnly(it: Pick<GroupLibraryItem, 'metadata'>): boolean {
  return it.metadata.linked_only === true;
}

/**
 * R304 — 來源標籤文字:`libraryView.source.<source>`,Meta 匯入多一截「· Meta」。
 * 五個呈現點(列表 / 合併列 / 燈箱 / 排序 / CSV)共用這一支,不要各自拼字串。
 */
export function itemSourceLabel(
  it: Pick<GroupLibraryItem, 'source' | 'metadata'>,
  t: TFunction<'library'>,
): string {
  const lt = t as unknown as (key: string) => string;
  const base = lt(`libraryView.source.${it.source}`);
  return itemAdPlatform(it) === 'meta' ? `${base} · ${lt('libraryView.platform.meta')}` : base;
}

/** 「專案」篩選維度的值(只有 project_asset 帶得出來;其餘 null = 不入選項)。 */
export function itemProjectName(it: GroupLibraryItem): string | null {
  return it.source === 'project_asset' ? it.sourceProjectName : null;
}

/**
 * 這顆素材的**圖檔來源已失效**(R295;BE 2026-09-01 `metadata.source_unavailable`)。
 *
 * 廣告平台的圖片 CDN 在來源失效時**不回 404,而是回一張佔位圖** —— 我們照樣下載成功、
 * 照樣存進 GCS。所以檔案是「有」的,只是內容不是那顆素材(dev 實測 5 顆「無料のストック
 * 画像」共用同一張 13,976B 的 Google "Stock image unavailable")。BE 用 sha256 精確命中
 * + 感知 hash 近似(≤6 位元)兩層偵測,命中就在入庫時標這個旗標。
 *
 * ⚠️ **判準是嚴格 `=== true`(fail-open)**:`metadata` 是自由 dict,認不出來就當
 * 「沒有失效」照常顯圖 —— 錯的方向只能是「漏標一張佔位圖」,不可以是「把正常素材
 * 標成失效」(那會讓使用者的真素材整批消失在空狀態後面)。BE 那側的偵測也是同一個方向。
 */
export function itemSourceUnavailable(it: Pick<GroupLibraryItem, 'metadata'>): boolean {
  return it.metadata.source_unavailable === true;
}

/**
 * 「上傳廣告」排序用:兩平台**較晚**的那一筆時間戳(都沒有 → 0 墊底)。
 *
 * ⚠️ 與 {@link matchesUploadedFilter} 同源判準 —— 一個算「有沒有上傳過」、一個算
 * 「最後一次上傳是何時」,兩者都必須看兩個平台,否則篩選與排序會互相打架。
 */
function latestUploadTs(
  it: { gaUploadedAt: string | null; metaUploadedAt: string | null },
  ts: (iso: string) => number,
): number {
  return Math.max(ts(it.gaUploadedAt ?? ''), ts(it.metaUploadedAt ?? ''));
}

/**
 * 「上傳廣告」篩選維度(R212 接 `ga_uploaded_at`;R319 起 **任一平台**有值就算)。
 *
 * ⚠️ 只看 `gaUploadedAt` 的話,只上過 Meta 的素材會被歸進「未上傳」——
 * 使用者用這個篩選找「還沒推上廣告的素材」時會把它們重複推一次。
 */
export function itemUploaded(it: GroupLibraryItem): boolean {
  return !!it.gaUploadedAt || !!it.metaUploadedAt;
}

// ─── 規格 / 時長(網格卡 gc-sp、gc-dur;D3 specMain/dur)────────────────────

function metaString(meta: Record<string, unknown>, key: string): string | null {
  const v = meta[key];
  return typeof v === 'string' && v.trim() ? v.trim() : null;
}

function metaNumber(meta: Record<string, unknown>, key: string): number | null {
  const v = meta[key];
  return typeof v === 'number' && Number.isFinite(v) && v > 0 ? v : null;
}

/**
 * 影片時長秒數。
 *
 * ⚠️ **BE 清單 wire 沒有這個欄位** —— `GroupLibraryItem` 只有自由格式的 `metadata`
 * (server-derived 的僅 `mime`)。所以這支是「有人寫過就讀得到、沒有就 null」,
 * 呼叫端據此決定要不要渲染時長藥丸(設計 `gridHTML()` 本身也是 `m.dur &&` 條件式)。
 * BE 之後補正式欄位時,改這一支即可。
 */
export function itemDurationSec(it: GroupLibraryItem): number | null {
  if (it.assetKind !== 'VIDEO') return null;
  return metaNumber(it.metadata, 'duration_seconds') ?? metaNumber(it.metadata, 'duration');
}

/**
 * 「規格」列(D3 `specMain()` = 比例 · 時長 · 檔案大小)。
 *
 * 同上:清單 wire 沒有尺寸 / 長度 / 檔案大小欄 —— **不偽造規格**,metadata 讀得到
 * 什麼就顯什麼,一項都讀不到就回 null(呼叫端退成所屬庫名)。
 * 順序照設計:尺寸 → 時長 → 檔案大小;`metadata.spec` 若已是成品字串則直接用。
 */
export function itemSpecLine(it: GroupLibraryItem): string | null {
  const preset = metaString(it.metadata, 'spec');
  if (preset) return preset;
  const parts: string[] = [];
  const w = metaNumber(it.metadata, 'width');
  const h = metaNumber(it.metadata, 'height');
  if (w && h) parts.push(`${w}×${h}`);
  const size = metaNumber(it.metadata, 'size_bytes');
  if (size) parts.push(`${(size / 1024 / 1024).toFixed(1)} MB`);
  return parts.length ? parts.join(' · ') : null;
}

/**
 * 日期拆成結構化欄位。**格式化不在這裡做** —— CIS v1.9 的主格寫法是
 * `8月 3, 2026`(月份名 + 日, 年),三語各自不同(en `Aug 3, 2026` / ja
 * `2026年8月3日`),所以組句必須經 i18n。這支只負責解析,好讓它保持純函式、
 * 可單測、且不把 react-i18next 拉進這個模組(契約鎖也 import 這裡)。
 *
 * 格式化用 [libraryDate](./libraryDate.ts) 的 `useLibraryDateFormat()`。
 *
 * @returns `null` = 不是合法日期(呼叫端原樣顯示 raw 字串)
 */
export interface DateFields {
  y: number;
  /** 1–12。 */
  m: number;
  d: number;
  /** 已補零的時分(副標 `HH:mm`)。 */
  hh: string;
  mm: string;
}

export function dateFields(iso: string): DateFields | null {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const p = (n: number) => String(n).padStart(2, '0');
  return {
    y: d.getFullYear(),
    m: d.getMonth() + 1,
    d: d.getDate(),
    hh: p(d.getHours()),
    mm: p(d.getMinutes()),
  };
}

/**
 * 「檢查日期」欄的兩行顯示 —— **badge.d 優先、`checkedAt` 墊底**(未檢查 → null)。
 *
 * 為什麼不統一吃 `checkedAt`:`badge.d` 是後端已用 **Asia/Taipei** 格式好的
 * `YYYY-MM-DD HH:mm`(docs/88 §0.3),而 `checkedAt` 是帶時區的 ISO,`new Date()`
 * 會轉成瀏覽者當地時間 —— 同一次檢查在兩個欄位可能顯示不同的鐘點。顯示以 badge.d
 * 為準(與報告頁、燈箱同一組數字);**排序走同一份優先序**(`sortLibraryItems` 的
 * `chkd` → `chkdTs`),顯示什麼日期就照什麼日期排,不會有「顯新排舊位」。
 */
export function checkedAtFields(
  it: GroupLibraryItem,
  badge: CheckBadge | null | undefined,
): DateFields | null {
  // badge.d 無時區後綴 → 以當地時間解析 = 原樣顯示那串數字(不位移)
  if (badge?.d) return dateFields(badge.d.replace(' ', 'T'));
  if (it.checkedAt) return dateFields(it.checkedAt);
  return null;
}

// ─── 排序(8 欄全可排序;library.js sorted/checkRank)────────────────────────

export type LibrarySortKey = 'name' | 'kind' | 'src' | 'srcName' | 'date' | 'imp' | 'chkd' | 'chk';

export interface LibrarySort {
  k: LibrarySortKey;
  d: 1 | -1;
}

/** 預設排序:產出日期新→舊(library.js st.sort)。 */
export const DEFAULT_LIBRARY_SORT: LibrarySort = { k: 'date', d: -1 };

/** 首點方向:date / imp / chk 降冪,其餘升冪(library.js:668-672)。 */
export function nextLibrarySort(cur: LibrarySort, k: LibrarySortKey): LibrarySort {
  if (cur.k === k) return { k, d: cur.d === 1 ? -1 : 1 };
  return { k, d: k === 'date' || k === 'imp' || k === 'chk' ? -1 : 1 };
}

/** 檢查結果排序權重(library.js checkRank:none 0 / err 1 / pass 2 / unk 3 / warn 4 / fail 5)。 */
export function checkRank(badge: CheckBadge | null | undefined): number {
  if (!badge) return 0;
  switch (badge.s) {
    case 'err':
      return 1;
    case 'pass':
      return 2;
    case 'unk':
      return 3;
    case 'warn':
      return 4;
    case 'fail':
      return 5;
    default:
      return 0;
  }
}

export interface LibrarySortCtx {
  /** badge 解析:**批次 store live results 優先、server `checkBadge` 墊底**(R212)。 */
  badgeOf(it: GroupLibraryItem): CheckBadge | null;
  /** i18n 後的類型 / 來源標籤(依顯示字排序,對齊設計 localeCompare 語意)。 */
  kindLabel(it: GroupLibraryItem): string;
  sourceLabel(it: GroupLibraryItem): string;
}

function ts(iso: string): number {
  const t = new Date(iso).getTime();
  return Number.isNaN(t) ? 0 : t;
}

function badgeTs(b: CheckBadge | null): number {
  if (!b?.d) return 0;
  return ts(b.d.replace(' ', 'T'));
}

/**
 * 「檢查日期」排序值 —— **badge.d 優先、`checkedAt` 墊底**,與 `checkedAtParts`
 * 的顯示優先序**同一份真相**(R212 訂正)。
 *
 * 為什麼不能反過來拿 `checkedAt` 當首選:重檢過的素材,badge.d 已經是這次的時間,
 * 而 list API 的 `checked_at` 還是上一次那筆 —— 顯示 08/07 的那列會排到顯示 08/05
 * 的下面,畫面與排序自相矛盾。順帶也把兩列的時間基準對齊(badge.d 無時區後綴、
 * `checkedAt` 是帶時區 ISO;兩者混用時,非 Asia/Taipei 時區下相近的兩筆會排反)。
 */
function chkdTs(it: GroupLibraryItem, ctx: LibrarySortCtx): number {
  const b = ctx.badgeOf(it);
  if (b?.d) return badgeTs(b);
  return it.checkedAt ? ts(it.checkedAt) : 0;
}

export function sortLibraryItems(
  items: GroupLibraryItem[],
  sort: LibrarySort,
  ctx: LibrarySortCtx,
): GroupLibraryItem[] {
  const { k, d } = sort;
  const cmp = (a: GroupLibraryItem, b: GroupLibraryItem): number => {
    switch (k) {
      case 'name':
        return itemDisplayName(a).localeCompare(itemDisplayName(b), 'zh-Hant');
      case 'kind':
        return ctx.kindLabel(a).localeCompare(ctx.kindLabel(b), 'zh-Hant');
      case 'src':
        return ctx.sourceLabel(a).localeCompare(ctx.sourceLabel(b), 'zh-Hant');
      case 'srcName':
        return (
          (itemSrcName(a) ?? '').localeCompare(itemSrcName(b) ?? '', 'zh-Hant') ||
          itemDisplayName(a).localeCompare(itemDisplayName(b), 'zh-Hant')
        );
      case 'date':
        return ts(a.createdAt) - ts(b.createdAt);
      case 'imp':
        // R212 接 `ga_uploaded_at`;R319 起取**兩平台較晚的那一筆**(未上傳 = 0 墊底)——
        // 只看 Google 的話,只上過 Meta 的素材會永遠排在最底,與欄位顯示的內容不一致。
        return latestUploadTs(a, ts) - latestUploadTs(b, ts);
      case 'chkd':
        return chkdTs(a, ctx) - chkdTs(b, ctx);
      case 'chk':
        return checkRank(ctx.badgeOf(a)) - checkRank(ctx.badgeOf(b));
      default:
        return 0;
    }
  };
  return items.slice().sort((a, b) => cmp(a, b) * d);
}
